import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaBuilding } from "react-icons/fa";
import dayjs from "dayjs";
import { useSelector, useDispatch } from "react-redux";
import styles from "../../master/academicConfiguration/Academiclist.module.css";
import {
  CustomButton,
  CustomTextInput,
  CustomDropdown,
  CustomDatePicker,
} from "../../../../components";
import AxiosInstance, {
  TokenService,
  sanitizeNameInput,
  hasInvalidNameChars,
} from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import {
  fetchClassOptions,
  fetchSectionOptions,
  clearSectionOptions,
} from "../../../../redux/Masterdata";

// Normalise a multi-select value into an array of string ids
const toValues = (val) =>
  Array.isArray(val)
    ? val.map((v) => (v && typeof v === "object" ? String(v.value) : String(v)))
    : val
      ? [String(val && typeof val === "object" ? val.value : val)]
      : [];

// Safely extract primitive id from a dropdown value (object or primitive)
const toId = (val) =>
  val && typeof val === "object" ? String(val.value) : val ? String(val) : "";

// Display API amounts (e.g. "200.00") as whole numbers (e.g. "200")
const toWholeAmount = (val) =>
  val === undefined || val === null || val === ""
    ? ""
    : String(Math.round(Number(val)));

const GroupFeeCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { translations } = useSelector((state) => state.language);
  const labels = translations?.labels || {};

  const { classOptions, sectionOptions } = useSelector(
    (state) => state.masterData,
  );

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const academic_year = clientData?.academic_year || "";
  const user_id = user?.id || "";

  const editId = location.state?.ID || null;
  const isEdit = Boolean(editId);
  // Academic year selected in the list page's filter dropdown, passed via
  // navigation state; falls back to the token's academic year when the list
  // page's dropdown had nothing selected.
  const viewAcademicYearId = location.state?.academicYearId || academic_year_id;
  // ---- Local state -------------------------------------------------------
  // ── Academic Year dropdown options (from API) ──
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [academicYearLoading, setAcademicYearLoading] = useState(false);

  const [feeTypeOptions, setFeeTypeOptions] = useState([]);
  // discountTypeOptions: { value, label, discountId, feeTypeId, rawAmount, type }
  // `value` is "<feeTypeId>:<discountId>" so the same discount offered under two
  // fee types stays selectable for each; feeTypeId remembers which fee type the
  // discount belongs to so it can be re-attached to the right fee_items row when
  // building the create/update payload; rawAmount is the discount's fixed value
  // from the API (read-only display).
  const [discountTypeOptions, setDiscountTypeOptions] = useState([]);
  // fee_type_id -> fee_items row id (needed so UPDATE can target the right row)
  const [feeItemIds, setFeeItemIds] = useState({});
  // "<feeTypeId>:<discountId>" -> fee_items row id, for the extra rows that
  // carry a fee type's 2nd and later discounts, so UPDATE reuses them instead
  // of appending a duplicate row on every save.
  const [discountItemIds, setDiscountItemIds] = useState({});

  const [formData, setFormData] = useState({
    academicYear: "",
    class_id: "",
    section_id: "",
    group_name: "",
    fee_types: [],
    discount_fee_types: [],
    description: "",
    demand_date: null,
    due_date: null,
    fine_amount: "",
  });
  const [feeAmounts, setFeeAmounts] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  // ---- Derived -----------------------------------------------------------
  const selectedFeeTypes = useMemo(
    () => feeTypeOptions.filter((o) => formData.fee_types.includes(o.value)),
    [feeTypeOptions, formData.fee_types],
  );

  const selectedDiscountTypes = useMemo(
    () =>
      discountTypeOptions.filter((o) =>
        formData.discount_fee_types.includes(o.value),
      ),
    [discountTypeOptions, formData.discount_fee_types],
  );

  const totalAmount = useMemo(
    () =>
      selectedFeeTypes.reduce(
        (sum, ft) => sum + (parseFloat(feeAmounts[ft.value]) || 0),
        0,
      ),
    [selectedFeeTypes, feeAmounts],
  );

  // A flat (type 0) discount adds its raw value as-is; a percentage
  // (type 1) discount is a % of the amount entered for the fee type it
  // belongs to, so it must be resolved against feeAmounts.
  const getDiscountAmount = useCallback(
    (dt) => {
      const rawValue = parseFloat(dt.rawAmount) || 0;
      if (Number(dt.type) === 1) {
        const feeAmount = parseFloat(feeAmounts[dt.feeTypeId]) || 0;
        return (feeAmount * rawValue) / 100;
      }
      return rawValue;
    },
    [feeAmounts],
  );

  const totalDiscountAmount = useMemo(
    () =>
      selectedDiscountTypes.reduce((sum, dt) => sum + getDiscountAmount(dt), 0),
    [selectedDiscountTypes, getDiscountAmount],
  );

  // ---- API: discounts for the selected fee type(s) -----------------------
  // academicYearId is passed explicitly (rather than read off formData/state)
  // so the caller controls exactly which year's discounts get fetched — this
  // matters during edit load, where the view response's academic_year_id is
  // known before formData has been updated with it.
  const fetchDiscountOptionsFor = async (feeTypeIds, academicYearId) => {
    if (!feeTypeIds.length || !academicYearId) return [];
    try {
      const res = await AxiosInstance.post(
        "schoolFees/feeGroup/feeTypeDropdown",
        {
          school_id,
          academic_year_id: Number(academicYearId),
          fee_type_id: feeTypeIds.map(Number),
        },
      );
      if (res?.data?.status) {
        const data = res.data.data || [];
        // A discount can be offered under more than one fee type, so options are
        // keyed by "<feeTypeId>:<discountId>" rather than by discount id alone.
        // Deduping by discount id would drop the second fee type's pairing and
        // leave that fee type's payload row with no discount at all.
        const feeTypesPerDiscount = {};
        data.forEach((ft) => {
          (ft.discounts || []).forEach((d) => {
            feeTypesPerDiscount[d.id] = (feeTypesPerDiscount[d.id] || 0) + 1;
          });
        });

        const options = [];
        data.forEach((ft) => {
          const feeTypeLabel = ft.name || ft.fee_type_name || "";
          (ft.discounts || []).forEach((d) => {
            options.push({
              value: `${ft.id}:${d.id}`,
              // Qualify the label with the fee type only when the same discount
              // is selectable under several of them, so the list stays readable.
              label:
                feeTypesPerDiscount[d.id] > 1 && feeTypeLabel
                  ? `${d.name} (${feeTypeLabel})`
                  : d.name,
              discountId: String(d.id),
              feeTypeId: String(ft.id),
              rawAmount: d.value ?? "",
              type: Number(d.type) || 0,
            });
          });
        });
        return options;
      }
    } catch (error) {
      console.error("Error fetching discount options:", error);
    }
    return [];
  };

  // ---- Mount -------------------------------------------------------------
  useEffect(() => {
    const init = async () => {
      if (school_id) {
        dispatch(fetchClassOptions({ id: school_id, academic_year }));
      }
      if (isEdit) loadGroupFeeDetail(editId);
    };
    init();
    return () => dispatch(clearSectionOptions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch Academic Year list on mount ──
  useEffect(() => {
    fetchAcademicYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id]);

  /** Academic Year list API: GET school/academic/{school_id} */
  const fetchAcademicYears = async () => {
    if (!school_id) return;
    setAcademicYearLoading(true);
    try {
      const response = await AxiosInstance.get(`/school/academic/${school_id}`);

      if (response?.data?.success && Array.isArray(response.data.data)) {
        const options = response.data.data
          .filter((item) => item.isActive === 1 || item.isUpcoming === 1)
          .map((item) => ({
            value: String(item.id),
            label: item.academic_year,
          }));
        setAcademicYearOptions(options);
      } else {
        setAcademicYearOptions([]);
      }
    } catch (error) {
      console.error("Error fetching academic years:", error);
      setAcademicYearOptions([]);
    } finally {
      setAcademicYearLoading(false);
    }
  };

  // Fetch sections whenever class changes
  useEffect(() => {
    if (formData.class_id) {
      dispatch(
        fetchSectionOptions({
          id: [parseInt(formData.class_id)],
          school_id: parseInt(school_id),
          staff_id: null,
          subject_staff: 1,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.class_id]);

  // ── Fetch Fee Type catalog whenever the selected Academic Year changes ──
  useEffect(() => {
    fetchFeeTypeOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id, formData.academicYear]);

  // ---- API: Fee Type catalog (used to populate the Fee Type dropdown) ----
  const fetchFeeTypeOptions = async () => {
    if (!school_id || !formData.academicYear) {
      setFeeTypeOptions([]);
      return;
    }
    try {
      let allData = [];
      let page = 1;
      let totalPages = 1;
      do {
        const res = await AxiosInstance.post("schoolFees/list/fees_type", {
          school_id,
          academic_id: Number(formData.academicYear),
          page,
          limit: "",
          search: "",
        });
        if (res?.data?.status) {
          allData = [...allData, ...(res.data.data || [])];
          totalPages = res.data.total_pages || 1;
        } else {
          break;
        }
        page += 1;
      } while (page <= totalPages);

      setFeeTypeOptions(
        allData.map((it) => ({ value: String(it.id), label: it.name })),
      );
    } catch (error) {
      console.error("Error fetching fee types:", error);
    }
  };

  // ---- Edit: load & prefill ---------------------------------------------
  const loadGroupFeeDetail = async (id) => {
    setLoading(true);
    try {
      const res = await AxiosInstance.post("schoolFees/feeGroup/GroupView", {
        id,
        school_id,
        academic_year_id: viewAcademicYearId,
      });
      if (res?.data?.status) {
        const d = res.data.data || {};
        const feeTypeIds = [
          ...new Set((d.fee_items || []).map((f) => String(f.fee_type_id))),
        ];
        // Each saved fee_items row carries at most one discount, so a fee type
        // with several discounts spans several rows. Collect every one of them,
        // re-keyed to "<feeTypeId>:<discountId>" to match the option values.
        const discountKeys = [
          ...new Set(
            (d.fee_items || [])
              .filter((f) => f.discount_id)
              .map((f) => `${f.fee_type_id}:${f.discount_id}`),
          ),
        ];

        const options = await fetchDiscountOptionsFor(
          feeTypeIds,
          d.academic_id,
        );
        setDiscountTypeOptions(options);

        setFormData({
          academicYear:
            d?.academic_id !== undefined && d?.academic_id !== null
              ? String(d.academic_id)
              : "",
          class_id: d.class_id ? String(d.class_id) : "",
          section_id: d.section_id ?? "",
          group_name: d.group_name ?? "",
          fee_types: feeTypeIds,
          discount_fee_types: discountKeys.filter((key) =>
            options.some((o) => o.value === key),
          ),
          description: d.description ?? "",
          demand_date: d.demand_date ? dayjs(d.demand_date) : null,
          due_date: d.due_date ? dayjs(d.due_date) : null,
          fine_amount:
            d.fine_amount !== undefined && d.fine_amount !== null
              ? String(Number(d.fine_amount))
              : "",
        });

        const fa = {};
        const itemIds = {};
        const discountIds = {};
        (d.fee_items || []).forEach((f) => {
          const feeTypeId = String(f.fee_type_id);
          const amount =
            f.amount !== undefined && f.amount !== null ? Number(f.amount) : 0;
          // Several rows can share a fee type — one per extra discount. The fee's
          // real amount sits on its primary row and the extra rows carry 0, so
          // the highest amount identifies the primary row. Without this the last
          // row would win and blank out the amount with a 0.
          if (fa[feeTypeId] === undefined || amount > Number(fa[feeTypeId])) {
            fa[feeTypeId] = String(amount);
            itemIds[feeTypeId] = f.id;
          }
          if (f.discount_id) {
            discountIds[`${feeTypeId}:${f.discount_id}`] = f.id;
          }
        });
        setFeeAmounts(fa);
        setFeeItemIds(itemIds);
        setDiscountItemIds(discountIds);
      }
    } catch (error) {
      console.error("Error loading group fee detail:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          labels.somethingWrong ||
          "Failed to fetch group fee details. Please try again.",
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Handlers ----------------------------------------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  /* Group Name rejects special characters and emojis as they are typed. */
  const handleGroupNameChange = (e) => {
    setFormData((p) => ({
      ...p,
      group_name: sanitizeNameInput(e.target.value),
    }));
    setErrors((p) => ({ ...p, group_name: "" }));
  };

  const handleClassChange = (value) => {
    const id = toId(value);
    setFormData((p) => ({ ...p, class_id: id, section_id: "" }));
    setErrors((p) => ({ ...p, class_id: "", section_id: "" }));
  };

  const handleDropdownChange = (name, value) => {
    const id = toId(value);
    setFormData((p) => ({ ...p, [name]: id }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleFeeTypesChange = async (val) => {
    const values = toValues(val);
    setFormData((p) => ({ ...p, fee_types: values }));
    setErrors((p) => ({ ...p, fee_types: "" }));

    setFeeAmounts((prev) => {
      const next = {};
      values.forEach((v) => {
        if (prev[v] != null) next[v] = prev[v];
      });
      return next;
    });

    if (!values.length) {
      setDiscountTypeOptions([]);
      setFormData((p) => ({ ...p, discount_fee_types: [] }));
      return;
    }

    const options = await fetchDiscountOptionsFor(
      values,
      formData.academicYear,
    );
    setDiscountTypeOptions(options);
    setFormData((p) => ({
      ...p,
      discount_fee_types: p.discount_fee_types.filter((id) =>
        options.some((o) => o.value === id),
      ),
    }));
  };

  const handleDiscountTypesChange = (val) => {
    const values = toValues(val);
    setFormData((p) => ({ ...p, discount_fee_types: values }));
    setErrors((p) => ({ ...p, discount_fee_types: "" }));
  };

  const handleFeeAmountChange = (id, value) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 7);
    setFeeAmounts((p) => ({ ...p, [id]: digitsOnly }));
    setErrors((p) => ({ ...p, [`fee_${id}`]: "" }));
  };

  const handleDateChange = (name, dayjsValue) => {
    setFormData((p) => ({ ...p, [name]: dayjsValue || null }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  // ---- Validation --------------------------------------------------------
  const validate = () => {
    const e = {};
    if (!formData.academicYear)
      e.academicYear =
        labels.academicYearRequired || "Academic Year is required";
    if (!formData.class_id)
      e.class_id = labels.classRequired || "Class is required";
    if (!formData.section_id)
      e.section_id = labels.sectionRequired || "Section is required";
    if (!formData.group_name?.trim())
      e.group_name = labels.groupNameRequired || "Group Name is required";
    else if (hasInvalidNameChars(formData.group_name))
      // Safety net for values loaded from the API in edit mode
      e.group_name =
        labels.specialCharactersNotAllowed ||
        "Special characters and emojis are not allowed";
    if (!formData.fee_types.length)
      e.fee_types = labels.feeTypeRequired || "Fee Type is required";

    formData.fee_types.forEach((id) => {
      if (feeAmounts[id] === "" || feeAmounts[id] == null)
        e[`fee_${id}`] = labels.amountRequired || "Amount is required";
    });
    if (!formData.demand_date)
      e.demand_date = labels.demandDateRequired || "Demand Date is required";
    if (!formData.due_date)
      e.due_date = labels.dueDateRequired || "Due Date is required";
    if (
      formData.demand_date &&
      formData.due_date &&
      formData.due_date.isBefore(formData.demand_date)
    ) {
      e.due_date =
        labels.dueDateAfterDemand || "Due Date must be after Demand Date";
    }
    if (formData.fine_amount === "" || formData.fine_amount == null)
      e.fine_amount = labels.fineAmountRequired || "Fine Amount is required";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => ({
    ...(isEdit ? { id: editId } : {}),
    school_id,
    academic_year_id: Number(formData.academicYear),
    class_id: formData.class_id,
    section_id: formData.section_id,
    group_name: formData.group_name.trim(),
    description: formData.description,
    // Every selected fee type gets a row, whether or not it carries a discount,
    // and whether it was loaded in edit mode or added just now.
    fee_items: selectedFeeTypes.flatMap((ft) => {
      // A discount belongs to whichever fee type it was fetched under, so ALL of
      // that fee type's selected discounts are re-attached to it here. The first
      // one rides on the fee type's own row (unchanged behaviour); each extra
      // discount becomes its own object in the same array.
      const discounts = selectedDiscountTypes.filter(
        (dt) => dt.feeTypeId === ft.value,
      );
      const [firstDiscount, ...extraDiscounts] = discounts;

      const feeRow = {
        ...(isEdit && feeItemIds[ft.value] ? { id: feeItemIds[ft.value] } : {}),
        fee_type_id: Number(ft.value),
        amount: Number(feeAmounts[ft.value]) || 0,
        discount_id: firstDiscount ? Number(firstDiscount.discountId) : null,
        discount_amount: firstDiscount
          ? getDiscountAmount(firstDiscount)
          : null,
      };

      // Same object shape as a fee row. `amount` is 0 so the fee itself is still
      // charged only once no matter how many discounts are attached to it.
      const extraRows = extraDiscounts.map((dt) => {
        const rowId = discountItemIds[`${ft.value}:${dt.discountId}`];
        return {
          ...(isEdit && rowId ? { id: rowId } : {}),
          fee_type_id: Number(ft.value),
          amount: 0,
          discount_id: Number(dt.discountId),
          discount_amount: getDiscountAmount(dt),
        };
      });

      return [feeRow, ...extraRows];
    }),
    total_amount: totalAmount,
    demand_date: formData.demand_date
      ? formData.demand_date.format("YYYY-MM-DD")
      : "",
    due_date: formData.due_date ? formData.due_date.format("YYYY-MM-DD") : "",
    fine_amount: Number(formData.fine_amount) || 0,
    ...(isEdit ? { updated_by: user_id } : { created_by: user_id }),
  });

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const response = isEdit
        ? await AxiosInstance.put("schoolFees/feeGroup/update", payload)
        : await AxiosInstance.post("schoolFees/feeGroup/create", payload);

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: isEdit
            ? labels.groupFeeUpdatedSuccess || "Group Fee updated successfully!"
            : labels.groupFeeCreatedSuccess ||
              "Group Fee created successfully!",
          redirectPath: "/user/listGroupFee",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            labels.somethingWrong ||
            "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error saving group fee:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          labels.somethingWrong ||
          "Failed to save group fee. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await AxiosInstance.delete(
        "schoolFees/feeGroup/GroupDelete",
        {
          data: { id: editId, deleted_by: user_id },
        },
      );

      if (response?.data?.status) {
        setDeleteModalOpen(false);
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message ||
            labels.groupFeeDeletedSuccess ||
            "Group Fee deleted successfully!",
          redirectPath: "/user/listGroupFee",
        });
      } else {
        setDeleteModalOpen(false);
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            labels.somethingWrong ||
            "Failed to delete group fee. Please try again.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error deleting group fee:", error);
      setDeleteModalOpen(false);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete group fee. Please try again.",
        redirectPath: "",
      });
    }
  };

  const handleBack = () => navigate("/user/listGroupFee");

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  const SectionTitle = ({ children }) => (
    <div className={styles.header}>
      <div className={styles.headerIcon}>
        <FaBuilding />
      </div>
      <h2 className={styles.headerTitle}>{children}</h2>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.container}>
        <SectionTitle>
          {isEdit
            ? labels.editGroupFee || "Edit Group Fee"
            : labels.createGroupFee || "Create Group Fee"}
        </SectionTitle>
        <div className={styles.emptyState}>
          {labels.loading || "Loading..."}
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? labels.success || "Success"
            : labels.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <PopupModal
        open={deleteModalOpen}
        title={labels.delete || "Confirm Delete"}
        message={
          labels.confirmDeleteMessage || "Are you sure you want to delete?"
        }
        onOk={handleDelete}
        onClose={() => setDeleteModalOpen(false)}
      />

      <div className={styles.container}>
        {/* Page header */}
        <SectionTitle>
          {isEdit
            ? labels.editGroupFee || "Edit Group Fee"
            : labels.createGroupFee || "Create Group Fee"}
        </SectionTitle>

        {/* Top fields */}
        <div className={styles.viewFormGrid}>
          <CustomDropdown
            label={labels.academicYear || "Academic Year"}
            name="academicYear"
            value={formData.academicYear}
            onChange={(v) => handleDropdownChange("academicYear", v)}
            options={academicYearOptions}
            placeholder={
              academicYearLoading
                ? labels.loading || "Loading..."
                : labels.selectAcademicYear || "Select academic year"
            }
            required
            error={errors.academicYear}
            disabled={isEdit || academicYearLoading}
          />
          <CustomDropdown
            label={labels.classLabel || "Class"}
            name="class_id"
            value={formData.class_id}
            onChange={handleClassChange}
            options={classOptions}
            placeholder={labels.selectClass || "Select Class"}
            required
            error={errors.class_id}
          />
          <CustomDropdown
            label={labels.sectionLabel || "Section"}
            name="section_id"
            value={formData.section_id}
            onChange={(v) => handleDropdownChange("section_id", v)}
            options={sectionOptions}
            placeholder={labels.selectSection || "Select Section"}
            required
            error={errors.section_id}
          />
          <CustomTextInput
            label={labels.groupName || "Group Name"}
            name="group_name"
            type="text"
            value={formData.group_name}
            onChange={handleGroupNameChange}
            placeholder={labels.enterGroupName || "Enter Group Name"}
            required
            error={errors.group_name}
          />
          <CustomDropdown
            isMulti
            label={labels.feeType || "Fee Type"}
            name="fee_types"
            value={formData.fee_types}
            onChange={handleFeeTypesChange}
            options={feeTypeOptions}
            placeholder={
              labels.selectOneOrMoreFeeType || "Select one or more Fee Type"
            }
            required
            error={errors.fee_types}
          />
        </div>

        {/* Discount Fees */}
        <div className={styles.viewFormGrid}>
          <CustomDropdown
            isMulti
            label={labels.discountFeeType || "Discount Fees"}
            name="discount_fee_types"
            value={formData.discount_fee_types}
            onChange={handleDiscountTypesChange}
            options={discountTypeOptions}
            placeholder={
              labels.selectDiscountFeeType || "Select one or more Discount"
            }
            error={errors.discount_fee_types}
          />
        </div>

        {/* Description */}
        <div className={styles.sectionSpacing}>
          <CustomTextInput
            label={`${labels.description || "Description"} ${labels.optional || "(Optional)"}`}
            name="description"
            type="textarea"
            rows={4}
            value={formData.description}
            onChange={handleInputChange}
            placeholder={
              labels.enterDescriptionMaxWords ||
              "Enter Description (Max : 100 words)"
            }
          />
        </div>

        {/* Fee List */}
        {selectedFeeTypes.length > 0 && (
          <div className={styles.sectionSpacing}>
            <SectionTitle>{labels.feeList || "Fee List"}</SectionTitle>
            <div className={styles.viewFormGrid}>
              {selectedFeeTypes.map((ft) => (
                <CustomTextInput
                  key={ft.value}
                  label={ft.label}
                  name={`fee_${ft.value}`}
                  type="text"
                  value={feeAmounts[ft.value] ?? ""}
                  onChange={(e) =>
                    handleFeeAmountChange(ft.value, e.target.value)
                  }
                  placeholder={labels.enterAmount || "Enter Amount"}
                  required
                  error={errors[`fee_${ft.value}`]}
                />
              ))}
              <CustomTextInput
                label={labels.totalAmount || "Total Amount"}
                name="total_amount"
                type="text"
                value={totalAmount}
                onChange={() => {}}
                disabled
                readOnly
              />
            </div>
          </div>
        )}

        {/* Discount Fee List */}
        {selectedDiscountTypes.length > 0 && (
          <div className={styles.sectionSpacing}>
            <SectionTitle>
              {labels.discountFeeList || "Discount Fees"}
            </SectionTitle>
            <div className={styles.viewFormGrid}>
              {selectedDiscountTypes.map((dt) => (
                <CustomTextInput
                  key={dt.value}
                  label={dt.label}
                  name={`discount_${dt.value}`}
                  type="text"
                  value={`${toWholeAmount(dt.rawAmount)}${Number(dt.type) === 1 ? "%" : ""}`}
                  onChange={() => {}}
                  disabled
                  readOnly
                />
              ))}
              <CustomTextInput
                label={labels.totalDiscountAmount || "Total Discount Amount"}
                name="total_discount_amount"
                type="text"
                value={toWholeAmount(totalDiscountAmount)}
                onChange={() => {}}
                disabled
                readOnly
              />
            </div>
          </div>
        )}

        {/* Group Fees Configuration */}
        <div className={styles.sectionSpacing}>
          <SectionTitle>
            {labels.groupFeesConfiguration || "Group Fees Configuration"}
          </SectionTitle>
          <div className={styles.viewFormGrid}>
            <CustomDatePicker
              label={labels.demandDate || "Demand Date"}
              name="demand_date"
              value={formData.demand_date}
              onChange={(val) => handleDateChange("demand_date", val)}
              placeholder={labels.selectDate || "Select Date"}
              minDate={dayjs()}
              required
              error={errors.demand_date}
            />
            <CustomDatePicker
              label={labels.dueDate || "Due Date"}
              name="due_date"
              value={formData.due_date}
              onChange={(val) => handleDateChange("due_date", val)}
              placeholder={labels.selectDate || "Select Date"}
              minDate={formData.demand_date || dayjs()}
              required
              error={errors.due_date}
            />
            <CustomTextInput
              label={labels.fineAmount || "Fine Amount"}
              name="fine_amount"
              type="text"
              value={formData.fine_amount}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                setFormData((p) => ({ ...p, fine_amount: val }));
                setErrors((p) => ({ ...p, fine_amount: "" }));
              }}
              placeholder={
                labels.enterFineAmount || "Enter Fine Amount (max 4 digits)"
              }
              required
              error={errors.fine_amount}
            />
          </div>
        </div>

        {/* Footer buttons */}
        <div className={styles.reminderFooter}>
          {isEdit ? (
            <>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.deleteButton}
                fullWidth={false}
                onClick={() => setDeleteModalOpen(true)}
                disabled={submitting}
              >
                {labels.delete || "DELETE"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.cancelButton}
                fullWidth={false}
                onClick={handleBack}
                disabled={submitting}
              >
                {labels.back || "BACK"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.addButton}
                fullWidth={false}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {labels.update || "UPDATE"}
              </CustomButton>
            </>
          ) : (
            <>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.cancelButton}
                fullWidth={false}
                onClick={handleBack}
                disabled={submitting}
              >
                {labels.cancel || "CANCEL"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.addButton}
                fullWidth={false}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {labels.createGroupFee || "Create Group Fee"}
              </CustomButton>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default GroupFeeCreate;
